import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { Box, useTheme } from "@mui-wrapper";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { ArrowDownIcon, Menu, MenuItem, Skeleton, Button, ButtonGroup } from "@flat-ui";
import { withEllipsis } from "@styles/common-styles";
import { OPEN_PROJECT_TARGET_ATTRIBUTE, getOpenProjectHref, getOpenProjectTargetName, trackOpenProject } from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { clickStyle } from "@utils/ui-utils";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
/**
 * Button to open a project in the Sphere Viewer or WebEditor
 */
export function OpenProjectButton({
  project,
  isLoading
}) {
  const theme = useTheme();
  const {
    trackAsyncEvent
  } = useTrackEvent();
  const projectLaunchSettings = useAppSelector(defaultProjectLaunchTargetSelector);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);
  const canOpenWebEditor = projectLaunchSettings.canOpenWebEditor;

  /**
   * Defines the target that this project should be opened in.
   */
  const openTargetProps = useMemo(() => {
    // The default target.
    const target = projectLaunchSettings.defaultOpenProjectTarget;
    const targetName = getOpenProjectTargetName(target);
    const targetHref = getOpenProjectHref({
      openTarget: target,
      projectId: project.id
    });
    // The target that is not the default one.
    const otherTarget = projectLaunchSettings.defaultOpenProjectTarget === ProjectLaunchTarget.sphereViewer ? ProjectLaunchTarget.webEditor : ProjectLaunchTarget.sphereViewer;
    const otherTargetName = getOpenProjectTargetName(otherTarget);
    const otherTargetHref = getOpenProjectHref({
      openTarget: otherTarget,
      projectId: project.id
    });
    return {
      target,
      targetName,
      targetHref,
      otherTarget,
      otherTargetName,
      otherTargetHref
    };
  }, [projectLaunchSettings.defaultOpenProjectTarget, project.id]);

  /** Returns the button to open the project in the default platform */
  const openProjectButton = useMemo(() => {
    // Workspaces that cannot open the WebEditor have a single option, so naming it is redundant.
    const openText = canOpenWebEditor ? `Open in ${openTargetProps.targetName}` : "Open project";

    // Layout only: the look comes from flat-ui's theme. Inside the button group the group
    // carries the margin instead.
    const sx = {
      width: {
        xs: canOpenWebEditor ? "90px" : "121px",
        sm: "auto"
      },
      ml: canOpenWebEditor ? 0 : "10px"
    };
    return <Button sx={sx} LinkComponent={Link}
    // Spread, because `to` and `target` belong to the router `Link` rather than to `ButtonProps`.
    {...{
      to: openTargetProps.targetHref,
      target: OPEN_PROJECT_TARGET_ATTRIBUTE
    }} onClick={() => {
      void triggerTrackOpenProject({
        clickType: "left button click",
        openTarget: openTargetProps.target
      });
    }} onAuxClick={() => {
      void triggerTrackOpenProject({
        clickType: "middle button click",
        openTarget: openTargetProps.target
      });
    }} onContextMenu={() => {
      void triggerTrackOpenProject({
        clickType: "open context menu",
        openTarget: openTargetProps.target
      });
    }}>
        {/* The button renders its content inside a flex container, so the
         ellipsis has to be applied to the label itself. On the button root it
         would only clip the label, without ever showing the ellipsis. */}
        <Box component="span" sx={withEllipsis}>
          {openText}
        </Box>
      </Button>;
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't need triggerTrackOpenProject as a dependency
  }, [openTargetProps, canOpenWebEditor]);

  /**
   * Tracks whenever a user opens a project, including the number of members and the target
   */
  async function triggerTrackOpenProject({
    clickType,
    openTarget
  }) {
    await trackOpenProject({
      openFrom: "projectDetails",
      numberOfMembers: project.members.length,
      openTarget,
      clickType,
      ...project,
      trackAsyncEvent
    });
  }
  if (isLoading) {
    return <Skeleton variant="rounded" width="157px" height="36px" sx={{
      ml: "10px"
    }} />;
  }
  if (!canOpenWebEditor) {
    // Without the WebEditor there is only one application to open the project in,
    // so the dropdown with the alternative target is not needed.
    return openProjectButton;
  }
  return <>
      <ButtonGroup ref={anchorRef} variant="contained" aria-label="Open project" sx={{
      ml: "10px"
    }}>
        {openProjectButton}
        <Button aria-label="More open options" aria-haspopup="menu" aria-expanded={isOpen} onClick={() => setIsOpen(wasPrevOpen => !wasPrevOpen)} icon={<ArrowDownIcon sx={{
        transform: isOpen ? "rotate(-180deg)" : "",
        transitionDuration: "0.3s",
        transitionProperty: "transform"
      }} />} />
      </ButtonGroup>
      <Menu open={isOpen} anchorEl={anchorRef.current} onClose={() => setIsOpen(false)} anchorOrigin={{
      vertical: "bottom",
      horizontal: "right"
    }} transformOrigin={{
      vertical: "top",
      horizontal: "right"
    }} minWidth="auto" sx={{
      "& .MuiMenu-paper": {
        mt: 0.5
      }
    }}>
        <Link target={OPEN_PROJECT_TARGET_ATTRIBUTE} to={openTargetProps.otherTargetHref} style={{
        color: theme.vars.palette.text.primary,
        textDecoration: "none"
      }}>
          <MenuItem onClick={() => {
          void triggerTrackOpenProject({
            clickType: "left button click",
            openTarget: openTargetProps.otherTarget
          });
        }} onAuxClick={() => {
          void triggerTrackOpenProject({
            clickType: "middle button click",
            openTarget: openTargetProps.otherTarget
          });
        }} onContextMenu={() => {
          void triggerTrackOpenProject({
            clickType: "open context menu",
            openTarget: openTargetProps.otherTarget
          });
        }} disableRipple label={`Open in ${openTargetProps.otherTargetName}`} highlightColor="primary.main" sx={{
          padding: "8px",
          fontSize: "12px",
          "&:active": {
            ...clickStyle
          }
        }} />
        </Link>
      </Menu>
    </>;
}